import { ref } from 'vue'

export function useDebounce () {
  const debounceTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null)

  /**
  *
  * @param {Function} method - The function to be executed after the delay.
  * @param {number} delay - The time in milliseconds to wait before executing the method.
  *
  * This function is used to prevent performance issues caused by calling a method too frequently.
  * It sets a timeout to delay the execution of the method and ensures that subsequent calls within
  * the delay period are ignored until the timeout completes.
  *
  * Example usage:
  * document.addEventListener('scroll', () => this.debounce(() => this.handleScroll(), 100), { passive: true })
  *
  * In the example, handleScroll() is wrapped with debounce to ensure it is not called more often than
  * every 100 milliseconds during scroll events.
  */

  function debounce (method: () => void, delay: number): void {
    if (!debounceTimeoutId.value) {
      debounceTimeoutId.value = setTimeout(() => {
        method()
        debounceTimeoutId.value = null
      }, delay)
    }
  }

  return { debounce, debounceTimeoutId }
}
